import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { ApiError } from '../../lib/api-client.js';
import { useAuth } from '../../lib/auth-context.js';

/**
 * License details for the signed-in license — `licenseType`,
 * `mainMetricId`, feature-gating flags, the owning client's GitHub-repo
 * coordinates (when GitHub backup is enabled), and the Workfront
 * workspace surface used by the custom-forms migration wizard. Shape
 * mirrors `GET /v1/license` exactly.
 */
export interface WorkfrontWorkspaceRef {
  id: string;
  name: string;
  url: string;
}

export interface LicenseDetails {
  licenseId: string;
  clientId: string;
  licenseType: string;
  enabled: boolean;
  /** Id of the license's "main metric" (a `kpi_metric` of the same license), or null. */
  mainMetricId: number | null;
  githubBackupEnabled: boolean;
  aiSummaryEnabled: boolean;
  workfrontBackupEnabled: boolean;
  logMetricsEnabled: boolean;
  client: {
    gitHubRepoOwner: string | null;
    gitHubRepoName: string | null;
    gitHubBranch: string | null;
  } | null;
  /** Primary Workfront workspace (set when license.workfrontConfigId is non-null). */
  primaryWorkfrontConfig: WorkfrontWorkspaceRef | null;
  /**
   * Additional Workfront workspaces linked through
   * `license_secondary_workfront_configs`. `ref` is the value the engine's
   * `resolveWorkspace` accepts (`secondary:<index>`); ordering is shared
   * between this list and the engine handler so indexes line up across
   * the round-trip.
   */
  secondaryWorkfrontConfigs: ReadonlyArray<WorkfrontWorkspaceRef & { ref: string }>;
}

/**
 * License context value. `details` is `null` while the initial fetch
 * is in flight; consumers should fall back to safe defaults during
 * that window (LicenseProvider renders a LoadingScreen, so most
 * downstream consumers never see the null state).
 *
 * `isPro()` mirrors the legacy `licenseStore.isPro()` helper. Used
 * across the SPA to gate paid affordances.
 *
 * `updateMainMetric()` writes through to `PATCH /v1/license`; on
 * success the query is invalidated so any other consumer reads the
 * fresh value.
 */
interface LicenseContextValue {
  details: LicenseDetails | null;
  isLoading: boolean;
  error: Error | null;
  isPro: () => boolean;
  isExpired: boolean;
  updateMainMetric: (mainMetricId: number | null) => Promise<void>;
}

const LicenseContext = createContext<LicenseContextValue | null>(null);

/**
 * Wraps the authenticated tree, fetches `/v1/license` once per
 * session, exposes the result via `useLicense()`. Mounts INSIDE
 * `<RequireAuth>` because the fetch needs a valid JWT.
 *
 * License-expired (`410 Gone` from the API → `error.status === 410`)
 * surfaces as `isExpired: true`; consumers should render
 * `LicenseExpiredPage` rather than the normal chrome.
 */
export function LicenseProvider({ children }: { children: ReactNode }): JSX.Element {
  const { session, api } = useAuth();
  const qc = useQueryClient();

  const query = useQuery<LicenseDetails, Error>({
    queryKey: ['license', session?.licenseId ?? 'anon'],
    queryFn: () => api.get<LicenseDetails>('/v1/license'),
    enabled: !!session,
    staleTime: 5 * 60_000,
    gcTime: 30 * 60_000,
    retry: (failureCount, err) => {
      // 410 = license expired; no point retrying. Same for 403/404 —
      // a fresh JWT won't change the underlying DB state.
      if (err instanceof ApiError && [410, 403, 404].includes(err.status)) return false;
      return failureCount < 2;
    },
  });

  const mutation = useMutation({
    mutationFn: (mainMetricId: number | null) =>
      api.patch<unknown>('/v1/license', { mainMetricId }),
    // Returned (not `void`ed) so `mutateAsync` resolves only after the
    // license refetch settles — callers never render the stale main metric.
    onSuccess: () => qc.invalidateQueries({ queryKey: ['license', session?.licenseId ?? 'anon'] }),
  });

  const isExpired =
    (query.error instanceof ApiError && query.error.status === 410) ||
    (query.data ? !query.data.enabled : false);

  const value = useMemo<LicenseContextValue>(
    () => ({
      details: query.data ?? null,
      isLoading: query.isLoading,
      error: (query.error as Error | null) ?? null,
      isPro: () => query.data?.licenseType === 'pro',
      isExpired,
      updateMainMetric: (mainMetricId) => mutation.mutateAsync(mainMetricId).then(() => undefined),
    }),
    [query.data, query.isLoading, query.error, isExpired, mutation],
  );

  return <LicenseContext.Provider value={value}>{children}</LicenseContext.Provider>;
}

export function useLicense(): LicenseContextValue {
  const ctx = useContext(LicenseContext);
  if (!ctx) throw new Error('useLicense must be used within <LicenseProvider>');
  return ctx;
}
